import React, { useMemo, useState } from "react";
import { Calendar, DollarSign, TrendingUp, Repeat } from "lucide-react";
import ScheduleCTA from "@/components/landing/ScheduleCTA";

/**
 * Interactive Dollar Cost Averaging (DCA) strategy section.
 * - User slides monthly investment + months
 * - Real-time chart shows: market price (volatile), DCA buys (constant $), final value
 * - Highlights the smoothing of cost over time.
 */

// Pseudo-random but deterministic market path (12 months)
const MARKET_PATH_12 = [
  100, 92, 105, 110, 88, 95, 102, 115, 96, 108, 120, 118,
];
// Extend for up to 36 months (3 yrs) by tiling with small drift
const buildPath = (months) => {
  const path = [];
  for (let i = 0; i < months; i++) {
    const base = MARKET_PATH_12[i % 12];
    const drift = 1 + Math.floor(i / 12) * 0.08; // ~8% annual drift
    path.push(Math.round(base * drift));
  }
  return path;
};

const DCAStrategy = () => {
  const [monthly, setMonthly] = useState(2000); // MXN per month
  const [months, setMonths] = useState(12);

  const { path, totalInvested, sharesAcquired, avgCost, finalPrice, finalValue, lumpSumValue } =
    useMemo(() => {
      const path = buildPath(months);
      let totalInvested = 0;
      let sharesAcquired = 0;
      path.forEach((price) => {
        totalInvested += monthly;
        sharesAcquired += monthly / price;
      });
      const finalPrice = path[path.length - 1];
      const finalValue = sharesAcquired * finalPrice;
      const avgCost = totalInvested / sharesAcquired;
      // Lump sum: invest all at month 0
      const initialPrice = path[0];
      const lumpSumShares = totalInvested / initialPrice;
      const lumpSumValue = lumpSumShares * finalPrice;
      return {
        path,
        totalInvested,
        sharesAcquired,
        avgCost,
        finalPrice,
        finalValue,
        lumpSumValue,
      };
    }, [monthly, months]);

  const gain = finalValue - totalInvested;
  const gainPct = (gain / totalInvested) * 100;

  // Chart geometry
  const W = 640;
  const H = 240;
  const PADDING = 28;
  const minP = Math.min(...path);
  const maxP = Math.max(...path);
  const range = maxP - minP || 1;
  const stepX = (W - PADDING * 2) / Math.max(path.length - 1, 1);
  const yOf = (p) => H - PADDING - ((p - minP) / range) * (H - PADDING * 2);
  const xOf = (i) => PADDING + i * stepX;
  const linePath = path.map((p, i) => `${i === 0 ? "M" : "L"} ${xOf(i)} ${yOf(p)}`).join(" ");

  const fmt = (n) =>
    new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      maximumFractionDigits: 0,
    }).format(n);

  return (
    <section
      id="dca"
      data-testid="dca-section"
      className="relative bg-[#0B0410] text-[#F9F6F0] py-24 lg:py-32 border-t border-white/5 overflow-hidden"
    >
      {/* Subtle background gradient */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(ellipse 60% 70% at 25% 50%, rgba(61,21,101,0.22) 0%, rgba(11,4,16,0) 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#E6D5B8]" />
              <span
                className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] font-medium"
                data-testid="dca-eyebrow"
              >
                Estrategia · Dollar Cost Averaging
              </span>
            </div>
            <h2
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.08]"
              data-testid="dca-headline"
            >
              Invierte la misma cantidad cada mes y deja que el{" "}
              <span className="italic text-[#E6D5B8]">tiempo trabaje</span> por ti.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base lg:text-lg text-[#F9F6F0]/70 font-light">
              DCA (Dollar Cost Averaging) es una estrategia que consiste en
              aportar la misma cantidad de dinero a intervalos regulares —
              independientemente del precio del mercado. Compras más cuando los
              precios están bajos y menos cuando están altos, suavizando el costo
              promedio en el largo plazo.
            </p>
          </div>
        </div>

        {/* Three-step explainer */}
        <div className="grid md:grid-cols-3 gap-6 mb-16" data-testid="dca-steps">
          {[
            {
              icon: Calendar,
              n: "01",
              title: "Defines un calendario",
              desc: "Aportas la misma cantidad — semanal, quincenal o mensual — durante un horizonte definido.",
            },
            {
              icon: Repeat,
              n: "02",
              title: "Compras sin emoción",
              desc: "El precio sube, baja, vuelve a subir. Tú sigues comprando. Sin adivinar el mercado, sin pánico.",
            },
            {
              icon: TrendingUp,
              n: "03",
              title: "Promedias el costo",
              desc: "Acumulas más participaciones cuando el precio está bajo. El costo promedio se vuelve tu aliado.",
            },
          ].map((s, i) => (
            <div
              key={i}
              data-testid={`dca-step-${i}`}
              className="bg-[#150A1F]/60 border border-white/10 p-7 hover:border-[#E6D5B8]/40 transition-colors duration-500"
            >
              <div className="flex items-center justify-between mb-5">
                <s.icon size={22} className="text-[#E6D5B8]" />
                <span className="font-display text-3xl text-[#E6D5B8]/40">
                  {s.n}
                </span>
              </div>
              <h3 className="font-display text-xl lg:text-2xl font-light mb-3">
                {s.title}
              </h3>
              <p className="text-sm text-[#F9F6F0]/65 font-light leading-relaxed">
                {s.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Interactive simulator */}
        <div className="bg-[#150A1F]/60 border border-white/10 backdrop-blur-sm p-8 lg:p-12" data-testid="dca-simulator">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-10">
            <div>
              <div className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] mb-3">
                Simulador
              </div>
              <h3 className="font-display text-2xl lg:text-3xl font-light">
                Visualiza tu estrategia DCA
              </h3>
            </div>

            {/* Inputs */}
            <div className="grid grid-cols-2 gap-4 w-full lg:w-auto">
              <label className="flex flex-col gap-2">
                <span className="text-[11px] tracking-luxury uppercase text-[#A397B4]">
                  Aportación mensual
                </span>
                <div className="flex items-center bg-[#0B0410]/60 border border-white/10 px-3 py-2">
                  <DollarSign size={14} className="text-[#E6D5B8] shrink-0" />
                  <input
                    type="number"
                    min="500"
                    step="500"
                    value={monthly}
                    onChange={(e) =>
                      setMonthly(Math.max(500, Number(e.target.value) || 0))
                    }
                    data-testid="dca-input-monthly"
                    className="w-full bg-transparent text-[#F9F6F0] outline-none px-2 font-display text-lg"
                  />
                </div>
              </label>

              <label className="flex flex-col gap-2">
                <span className="text-[11px] tracking-luxury uppercase text-[#A397B4]">
                  Plazo (meses)
                </span>
                <select
                  value={months}
                  onChange={(e) => setMonths(Number(e.target.value))}
                  data-testid="dca-input-months"
                  className="bg-[#0B0410]/60 border border-white/10 px-3 py-2 text-[#F9F6F0] outline-none font-display text-lg appearance-none"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23E6D5B8' d='M6 8L0 0h12z'/%3E%3C/svg%3E\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 12px center",
                    paddingRight: "32px",
                  }}
                >
                  <option value={6}>6 meses</option>
                  <option value={12}>12 meses (1 año)</option>
                  <option value={24}>24 meses (2 años)</option>
                  <option value={36}>36 meses (3 años)</option>
                  <option value={60}>60 meses (5 años)</option>
                  <option value={120}>120 meses (10 años)</option>
                  <option value={180}>180 meses (15 años)</option>
                  <option value={240}>240 meses (20 años)</option>
                  <option value={300}>300 meses (25 años)</option>
                </select>
              </label>
            </div>
          </div>

          {/* Chart + dots */}
          <div className="bg-[#0B0410]/60 border border-white/10 p-4 sm:p-6 mb-8">
            <svg
              viewBox={`0 0 ${W} ${H}`}
              className="w-full h-auto"
              preserveAspectRatio="none"
              data-testid="dca-chart"
            >
              <defs>
                <linearGradient id="dcaArea" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#E6D5B8" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#E6D5B8" stopOpacity="0" />
                </linearGradient>
              </defs>
              {/* Gridlines */}
              {[0.25, 0.5, 0.75].map((p) => (
                <line
                  key={p}
                  x1={PADDING}
                  x2={W - PADDING}
                  y1={PADDING + (H - PADDING * 2) * p}
                  y2={PADDING + (H - PADDING * 2) * p}
                  stroke="#F9F6F0"
                  strokeOpacity="0.06"
                  strokeDasharray="2 4"
                />
              ))}
              {/* Area under curve */}
              <path
                d={`${linePath} L ${xOf(path.length - 1)} ${H - PADDING} L ${PADDING} ${H - PADDING} Z`}
                fill="url(#dcaArea)"
              />
              {/* Price line */}
              <path
                d={linePath}
                fill="none"
                stroke="#E6D5B8"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              {/* DCA buy dots — adaptive density */}
              {path.map((p, i) => {
                // Show fewer dots for long horizons to keep chart readable
                const stepDot = path.length > 60 ? Math.ceil(path.length / 40) : 1;
                if (i % stepDot !== 0 && i !== path.length - 1) return null;
                const r = path.length > 120 ? 2.5 : path.length > 36 ? 3.2 : 4;
                return (
                  <circle
                    key={i}
                    cx={xOf(i)}
                    cy={yOf(p)}
                    r={r}
                    fill="#3D1565"
                    stroke="#FBEFD3"
                    strokeWidth="1.2"
                  />
                );
              })}
            </svg>
            <div className="flex items-center justify-between mt-4 text-[11px] tracking-luxury uppercase text-[#A397B4]">
              <div className="flex items-center gap-2">
                <span className="inline-block w-3 h-px bg-[#E6D5B8]" />
                Precio de mercado
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#3D1565] border border-[#FBEFD3]" />
                {months > 60 ? "Aportaciones DCA (muestreadas)" : "Cada compra DCA"}
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <ResultBox
              label="Total invertido"
              value={fmt(totalInvested)}
              sub={`${months} aportes de ${fmt(monthly)}`}
              testid="dca-result-invested"
            />
            <ResultBox
              label="Costo promedio"
              value={`$${avgCost.toFixed(2)}`}
              sub={`vs. precio final $${finalPrice.toFixed(2)}`}
              testid="dca-result-avg"
            />
            <ResultBox
              label="Valor final del portafolio"
              value={fmt(finalValue)}
              sub={`${sharesAcquired.toFixed(2)} participaciones`}
              highlight
              testid="dca-result-final"
            />
            <ResultBox
              label="Ganancia neta"
              value={`${gain >= 0 ? "+" : ""}${fmt(gain)}`}
              sub={`${gainPct >= 0 ? "+" : ""}${gainPct.toFixed(1)}% sobre lo invertido`}
              positive={gain >= 0}
              testid="dca-result-gain"
            />
          </div>

          {/* DCA vs Lump sum comparison */}
          <div className="mt-8 pt-8 border-t border-white/10 grid md:grid-cols-2 gap-6 text-sm font-light">
            <div className="flex items-start gap-3">
              <span className="inline-block w-2 h-2 rounded-full bg-[#E6D5B8] mt-1.5 shrink-0" />
              <p className="text-[#F9F6F0]/75">
                Con <span className="text-[#E6D5B8] font-medium">DCA</span> tu portafolio vale{" "}
                <span className="text-[#E6D5B8] font-medium">{fmt(finalValue)}</span> tras{" "}
                {months} meses aportando constantemente.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <span className="inline-block w-2 h-2 rounded-full bg-[#A397B4] mt-1.5 shrink-0" />
              <p className="text-[#F9F6F0]/75">
                Si hubieras invertido todo al inicio (lump sum), valdría{" "}
                <span className="text-[#F9F6F0]">{fmt(lumpSumValue)}</span> — más volátil,
                más sensible al timing.
              </p>
            </div>
          </div>

          <p className="mt-8 text-xs text-[#A397B4] font-light italic">
            * Simulación ilustrativa basada en una trayectoria hipotética del
            mercado. Rendimientos pasados no garantizan rendimientos futuros. La
            estrategia DCA reduce el riesgo de timing, no elimina el riesgo de
            mercado.
          </p>
        </div>

        {/* CTA banner */}
        <div className="mt-12">
          <ScheduleCTA
            eyebrow="Empieza hoy tu estrategia"
            testId="dca-cta"
          />
        </div>
      </div>
    </section>
  );
};

const ResultBox = ({ label, value, sub, highlight, positive, testid }) => (
  <div
    data-testid={testid}
    className={`p-5 border ${
      highlight
        ? "bg-[#3D1565]/20 border-[#3D1565]/60"
        : "bg-[#0B0410]/60 border-white/10"
    }`}
  >
    <div className="text-[10px] tracking-luxury uppercase text-[#A397B4] mb-2">
      {label}
    </div>
    <div
      className={`font-display text-2xl lg:text-[1.65rem] leading-tight ${
        positive === false ? "text-red-300" : "text-[#E6D5B8]"
      }`}
    >
      {value}
    </div>
    <div className="text-[11px] text-[#F9F6F0]/55 mt-2 font-light">{sub}</div>
  </div>
);

export default DCAStrategy;
